import type { Drama } from '@/types/content';

interface NextEpisodeSectionProps {
  drama: Drama;
}

export default function NextEpisodeSection({ drama }: NextEpisodeSectionProps) {
  const next = drama.nextEpisode;

  return (
    <section className="w-full border-b border-background-200 bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-content px-4 md:px-6">
        {next ? (
          <div className="overflow-hidden rounded-lg border border-primary-200 bg-background-50">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="flex flex-col justify-between bg-primary-500 p-6 text-background-50 md:p-8 lg:col-span-4">
                <div>
                  <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-background-100/80">
                    <i className="ri-notification-3-line animate-pulse-soft" aria-hidden="true" />
                    Next Episode
                  </p>
                  <h2 className="mt-4 font-heading text-2xl font-bold md:text-3xl">下一集预告</h2>
                  <p className="mt-3 text-sm leading-7 text-background-100/90">
                    官方预告放出后我们已在追踪表更新，先看这一集会发生什么。
                  </p>
                </div>
                <div className="mt-8">
                  <p className="text-xs text-background-100/80">预计上线</p>
                  <p className="font-display mt-1 text-3xl">{next.date}</p>
                </div>
              </div>

              <div className="p-6 md:p-8 lg:col-span-8">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-md bg-primary-50 px-3 py-1 text-xs text-primary-700">
                    第 {next.no} 集
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">{next.title}</h3>
                </div>
                <p className="mt-5 text-sm leading-8 text-foreground-700">{next.preview}</p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href="#episodes"
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-secondary-900 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-secondary-800"
                  >
                    <i className="ri-history-line" aria-hidden="true" />
                    回看上一集剧情
                  </a>
                  <a
                    href="#synopsis"
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    <i className="ri-book-open-line" aria-hidden="true" />
                    全季剧情梗概
                  </a>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-5 rounded-lg border border-background-200 bg-background-50 p-6 md:flex-row md:items-center md:justify-between md:p-8">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                <i className="ri-checkbox-circle-line text-xl" aria-hidden="true" />
              </span>
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">全季已完结</h2>
                <p className="mt-2 max-w-2xl text-sm leading-7 text-foreground-600">
                  《{drama.title}》共 {drama.totalEpisodes} 集已全部更新完毕，不再有下一集预告。可以按更新顺序回看剧情，
                  或直接阅读全季剧情梗概回忆整条主线。
                </p>
              </div>
            </div>
            <a
              href="#episodes"
              className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 md:w-auto"
            >
              <i className="ri-play-list-line" aria-hidden="true" />
              按顺序回看
            </a>
          </div>
        )}
      </div>
    </section>
  );
}